import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, Form, Input, Select, DatePicker, Button, Table, Tag, Space, message, Modal, Dropdown, Pagination, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { MoreOutlined } from '@ant-design/icons';
import { getMyDebts, deleteDebt, auditProcess, restoreDebt } from '../../services/debt';
import { DebtTypeOptions, labelOfDebtStatus, labelOfDebtType, toSelectOptions } from '../../constants/debtEnums';
import { formatAmountText } from '../../utils/numberFormat';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import './DebtDraftsPage.css';

/**
 * 债权草稿
 * 职责：管理“草稿/撤回”状态的债权记录，支持筛选、排序、分页、编辑、提交发布、删除与撤回后恢复。
 * 说明：
 * - 列表数据走统一的“我的债权列表”接口，通过 status=DRAFT 过滤草稿数据
 * - 前端维护分页/排序状态并透传到后端，确保不同债权页交互一致
 */
const DEFAULT_PAGINATION = { current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 };
const DEFAULT_SORTER = { field: 'update_time', order: 'desc' };
const normalizePageSize = (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE);

/**
 * 操作列：智能展示 + 溢出折叠
 * - 根据记录状态动态生成可用操作
 * - 当容器宽度不足时，自动把尾部操作折叠进“更多”下拉
 */
const ActionCell = ({ record, layoutKey, actionWidth, onEditDraft, onPublishDraft, onRestoreDraft, onDeleteDraft }) => {
  const containerRef = useRef(null);

  const candidates = useMemo(() => {
    if (String(record?.status) === 'REVOKED') {
      return [
        { key: 'restoreDraft', label: '恢复草稿' },
        { key: 'deleteDraft', label: '删除' },
      ];
    }
    return [
      { key: 'editDraft', label: '编辑' },
      { key: 'publishDraft', label: '发布' },
      { key: 'deleteDraft', label: '删除' },
    ];
  }, [record?.status]);

  const [inlineKeys, setInlineKeys] = useState(() => candidates.map(a => a.key));

  useEffect(() => {
    // 响应候选操作与布局变化（移动端/桌面）重新计算可展示的操作集合
    setInlineKeys(candidates.map(a => a.key));
  }, [candidates, layoutKey]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // 溢出判断：如果超出容器宽度，则逐步减少 inline 操作，把最后一个挪到下拉里
    const isOverflow = el.scrollWidth > el.clientWidth + 1;
    if (isOverflow && inlineKeys.length > 1) {
      const next = inlineKeys.slice(0, -1);
      if (next.join('|') !== inlineKeys.join('|')) {
        setInlineKeys(next);
      }
    }
  }, [candidates, inlineKeys]);

  const inlineActions = candidates.filter(a => inlineKeys.includes(a.key));
  const dropdownActions = candidates.filter(a => !inlineKeys.includes(a.key));

  const handleAction = async (key) => {
    if (key === 'editDraft') {
      onEditDraft?.(record);
      return;
    }
    if (key === 'publishDraft') {
      await onPublishDraft?.(record);
      return;
    }
    if (key === 'restoreDraft') {
      await onRestoreDraft?.(record);
      return;
    }
    if (key === 'deleteDraft') {
      onDeleteDraft?.(record);
    }
  };

  return (
    <div
      ref={containerRef}
      className="c-actions-inline c-actions-cell"
      style={{ '--actions-cell-width': actionWidth }}
    >
      {inlineActions.map(action => (
        <Button
          key={action.key}
          type="link"
          className="c-btn-link-compact"
          onClick={() => handleAction(action.key)}
        >
          {action.label}
        </Button>
      ))}

      {dropdownActions.length > 0 && (
        <Dropdown
          trigger={['click']}
          menu={{
            items: dropdownActions.map(a => ({ key: a.key, label: a.label })),
            onClick: async ({ key }) => {
              await handleAction(key);
            },
          }}
        >
          <Button type="link" className="c-btn-link-compact c-actions-more" icon={<MoreOutlined />} aria-label="更多操作" />
        </Dropdown>
      )}
    </div>
  );
};

const DebtDraftsPage = () => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorter, setSorter] = useState(DEFAULT_SORTER);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [dateRangeSelecting, setDateRangeSelecting] = useState(null);

  const disabledOccurrenceDate = useCallback((current) => {
    if (!current) return false;
    const range = Array.isArray(dateRangeSelecting) ? dateRangeSelecting : [];
    const start = range[0];
    const end = range[1];
    if (start && !end) {
      // 交互优化：当用户先选了开始日期但还没选结束日期时，限制可选范围在开始日期±1个月
      const min = start.subtract(1, 'month');
      const max = start.add(1, 'month');
      return current.isBefore(min, 'day') || current.isAfter(max, 'day');
    }
    return false;
  }, [dateRangeSelecting]);

  const fetchList = useCallback(async (pageCfg, sortCfg) => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const params = {
        page: pageCfg.current,
        size: pageCfg.pageSize,
        status: 'DRAFT',
        debt_type: values.debt_type,
        keyword: values.keyword,
        // 发生日：后端按天解析并做范围校验（开始/结束日期均存在时 ≤ 1 个月）
        start_date: values.date_range && values.date_range[0] ? values.date_range[0].format('YYYY-MM-DD') : undefined,
        end_date: values.date_range && values.date_range[1] ? values.date_range[1].format('YYYY-MM-DD') : undefined,
        sort_field: sortCfg.field,
        sort_order: sortCfg.order,
        need_total: pageCfg.current === 1,
      };
      const { data } = await getMyDebts(params);
      setRecords(Array.isArray(data?.records) ? data.records : []);
      const totalFromApi = data?.total;
      setPagination({
        current: Number(data?.current || pageCfg.current),
        pageSize: normalizePageSize(data?.size || pageCfg.pageSize),
        total: Number(totalFromApi ?? (pageCfg.total ?? 0)),
      });
    } catch (e) {
      message.error('加载草稿列表失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    // 首次进入：按默认分页与排序拉取列表
    fetchList({ current: 1, pageSize: DEFAULT_PAGINATION.pageSize }, DEFAULT_SORTER);
  }, [fetchList]);

  // 查询：重置到第一页并按当前排序条件查询
  const handleSearch = (values) => {
    const range = values?.date_range;
    const start = Array.isArray(range) ? range[0] : null;
    const end = Array.isArray(range) ? range[1] : null;
    if (start && end) {
      // 前端先做一次提示校验，避免无效请求；后端仍会兜底校验
      if (end.isBefore(start, 'day')) {
        message.error('发生日开始日期不能晚于结束日期');
        return;
      }
      if (end.isAfter(start.add(1, 'month'), 'day')) {
        message.error('发生日查询范围不能超过一个月');
        return;
      }
    }
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchList({ current: 1, pageSize: pagination.pageSize }, sorter);
  };

  // 重置筛选与排序
  const handleReset = () => {
    form.resetFields();
    setDateRangeSelecting(null);
    setPagination(prev => ({ ...prev, current: 1 }));
    setSorter(DEFAULT_SORTER);
    fetchList({ current: 1, pageSize: pagination.pageSize }, DEFAULT_SORTER);
  };

  // 表格交互：同步分页与排序到本地状态，并重新拉取数据
  const handleTableChange = (pag, filters, sorterArg) => {
    // antd Table sorter.order: ascend/descend -> 后端 sort_order: asc/desc
    const order = sorterArg?.order ? (sorterArg.order === 'ascend' ? 'asc' : 'desc') : sorter.order;
    const field = sorterArg?.field || sorter.field;
    const nextSorter = { field, order };
    setSorter(nextSorter);
    fetchList(pagination, nextSorter);
  };

  const handlePaginationChange = (page, pageSize) => {
    const nextPageSize = normalizePageSize(pageSize);
    const shouldResetToFirstPage = nextPageSize !== pagination.pageSize;
    const nextCurrent = shouldResetToFirstPage ? 1 : page;
    const nextPagination = { ...pagination, current: nextCurrent, pageSize: nextPageSize };
    setPagination(nextPagination);
    fetchList(nextPagination, sorter);
  };

  const handleRestore = async (id) => {
    try {
      await restoreDebt(id);
      message.success('已恢复为草稿');
      const next = { current: 1, pageSize: pagination.pageSize };
      setPagination(prev => ({ ...prev, current: 1 }));
      fetchList(next, sorter);
    } catch (e) {
      message.error('恢复草稿失败');
    }
  };

  const handleAudit = async (id) => {
    try {
      await auditProcess(id);
      message.success('已提交发布');
      const next = { current: 1, pageSize: pagination.pageSize };
      setPagination(prev => ({ ...prev, current: 1 }));
      fetchList(next, sorter);
    } catch (e) {
      // 使用后端返回的实际错误消息
      const errorMsg = e.message || e.response?.data?.message || '发布失败，请稍后重试';
      message.error(errorMsg);
    }
  };

  const openDeleteConfirm = (id) => {
    setConfirmId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmId) return;
    try {
      setConfirmLoading(true);
      await deleteDebt(confirmId);
      message.success('删除成功');
      setConfirmOpen(false);
      setConfirmId(null);
      const next = { current: 1, pageSize: pagination.pageSize };
      setPagination(prev => ({ ...prev, current: 1 }));
      fetchList(next, sorter);
    } catch (e) {
      message.error('删除失败');
    } finally {
      setConfirmLoading(false);
    }
  };

  const columns = [
    { title: '编号', dataIndex: 'debtNo', key: 'debtNo', width: 160 },
    {
      title: '债权名称',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (v, record) => (
        <Typography.Link className="u-ellipsis-inline font-medium" onClick={() => navigate(`/debt-management/detail/${record.id}`)}>
          {v || '-'}
        </Typography.Link>
      ),
    },
    { title: '债权类型', dataIndex: 'debtType', key: 'debtType', width: 140, render: (v) => labelOfDebtType(v) },
    { title: '本金金额', dataIndex: 'principalAmount', key: 'principalAmount', width: 120, sorter: true, align: 'right', className: 'u-tabular-nums', render: (v) => formatAmountText(v) },
    { title: '合计金额', dataIndex: 'totalAmount', key: 'totalAmount', width: 120, sorter: true, align: 'right', className: 'u-tabular-nums', render: (v) => formatAmountText(v) },
    { title: '发生日', dataIndex: 'debtOccurrenceDate', key: 'debtOccurrenceDate', width: 120, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    { title: '债权状态', dataIndex: 'status', key: 'status', width: 120, render: (v) => {
      const color = v === 'REVOKED' ? 'red' : 'default';
      return <Tag color={color}>{labelOfDebtStatus(v)}</Tag>;
    } },
    { title: '更新时间', dataIndex: 'updateTime', key: 'updateTime', width: 160, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '' },
    {
      title: '操作',
      key: 'action',
      width: isMobile ? 220 : 340,
      render: (_, record) => (
        <ActionCell
          record={record}
          layoutKey={isMobile ? 'mobile' : 'desktop'}
          actionWidth={isMobile ? '220px' : '340px'}
          onEditDraft={() => navigate(`/debt-management/publish?id=${record.id}`)}
          onPublishDraft={() => handleAudit(record.id)}
          onRestoreDraft={() => handleRestore(record.id)}
          onDeleteDraft={() => openDeleteConfirm(record.id)}
        />
      ),
    },
  ];

  return (
    <div className="page-container p-debt-management-debt-drafts">
      <div className="page-content page-content-wide space-y-md">
        <div className="page-title">债权草稿</div>

        <Card className="card-compact">
          <Form layout={isMobile ? 'vertical' : 'inline'} form={form} onFinish={handleSearch}>
            <Form.Item name="keyword" label="关键词">
              <Input placeholder="债权名称/编号" allowClear style={{ width: isMobile ? '100%' : 200 }} />
            </Form.Item>
            <Form.Item name="debt_type" label="类型">
              <Select allowClear style={{ width: isMobile ? '100%' : 180 }} options={toSelectOptions(DebtTypeOptions)} />
            </Form.Item>
            <Form.Item name="date_range" label="发生日">
              <DatePicker.RangePicker
                allowEmpty={[true, true]}
                popupClassName="c-range-picker-single-panel"
                style={{ width: isMobile ? '100%' : undefined }}
                disabledDate={disabledOccurrenceDate}
                onCalendarChange={(dates) => setDateRangeSelecting(dates)}
                onChange={(dates) => setDateRangeSelecting(dates)}
              />
            </Form.Item>
            <Form.Item>
              <Space wrap>
                <Button type="primary" htmlType="submit">查询</Button>
                <Button onClick={handleReset}>重置</Button>
                <Button onClick={() => navigate('/debt-management/publish')}>新建草稿</Button>
                <Button onClick={() => navigate('/debt-management/batch-publish')}>批量导入</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <Card className="card-compact">
          <Table
            rowKey="id"
            loading={loading}
            dataSource={records}
            columns={columns}
            size={isMobile ? 'small' : 'middle'}
            pagination={false}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
          />
          <div className="c-pagination-bar">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              simple={isMobile}
              showSizeChanger={isMobile ? false : PAGINATION_ENABLE_SIZE_CHANGER}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              onChange={handlePaginationChange}
            />
          </div>
        </Card>
      </div>

      <Modal
        title="确认删除该草稿？"
        open={confirmOpen}
        centered
        onOk={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
        confirmLoading={confirmLoading}
        okText="删除"
        cancelText="取消"
      >
        未发布草稿将物理删除；已发布后撤回的进行逻辑删除。
      </Modal>
    </div>
  );
};

export default DebtDraftsPage;
